/**
 * One rule for "has this program run out", shared by the header issue picker,
 * the program cards and direct issuance (the Validate form). TEN-10 made it
 * one rule for the usage inventory; TEN-09 extends the same rule to the
 * commercial bound (a chargeback program's monthly spend limit, a prepaid
 * program's block).
 *
 * ⚠️ UNKNOWN IS NOT ZERO, on both axes. A capped program whose `remaining`
 * the API did not report, and a program whose `commercial` is absent or
 * `unknown`, both stay issuable: the server re-checks every bound on
 * redemption and refuses a spent program, so a missing answer must never read
 * as "used up". Only a REPORTED `remaining <= 0` on a capped program, or a
 * reported `commercial.status === 'reached'`, is used up.
 *
 * The two bounds are kept apart on purpose. The inventory counts redemptions
 * per window; a prepaid block counts grants issued. A program can be out of
 * one and not the other, and the label says which.
 */
import type { ValidationCommercialAvailability } from './api'

export interface InventoryFields {
  inventory?: { cap: number } | null
  remaining?: number | null
}

export interface EligibilityFields extends InventoryFields {
  commercial?: Pick<ValidationCommercialAvailability, 'bound' | 'status'> | null
}

export type InventoryState = 'uncapped' | 'unknown' | 'available' | 'exhausted'

export function inventoryState(o: InventoryFields): InventoryState {
  if (!o.inventory) return 'uncapped'
  if (typeof o.remaining !== 'number' || !Number.isFinite(o.remaining)) return 'unknown'
  return o.remaining <= 0 ? 'exhausted' : 'available'
}

export const isInventoryExhausted = (o: InventoryFields): boolean => inventoryState(o) === 'exhausted'

/**
 * The commercial bound, as the server reported it.
 *   unbounded  nothing commercial can run out (included, uncapped, not set)
 *   open       bounded, and the bound admits another redemption
 *   reached    bounded and spent: redemption is refused
 *   unknown    not reported, or the server could not count. Issuable.
 */
export type CommercialState = 'unbounded' | 'open' | 'reached' | 'unknown'

export function commercialState(o: EligibilityFields): CommercialState {
  const c = o.commercial
  if (!c) return 'unknown'
  if (c.status === 'unknown') return 'unknown'
  if (c.bound === 'none') return 'unbounded'
  return c.status === 'reached' ? 'reached' : 'open'
}

export const isCommercialBoundReached = (o: EligibilityFields): boolean => commercialState(o) === 'reached'

/** THE shared rule: a reported bound, of either kind, is spent. */
export const isOfferUsedUp = (o: EligibilityFields): boolean =>
  isInventoryExhausted(o) || isCommercialBoundReached(o)

export const USED_UP_LABEL = 'Used up until reset'
export const LIMIT_REACHED_LABEL = 'Monthly limit reached'
export const BLOCK_USED_UP_LABEL = 'Block used up'

/**
 * What a used-up program's button and picker chip say. The commercial bound
 * leads when both are spent: it is the one only the operator can lift, while
 * the inventory resets by itself. The words name the arrangement for staff;
 * the kiosk's guest-facing wall stays the one shared "No validations
 * remaining" panel (rev 15 §3.16).
 */
export function usedUpLabel(o: EligibilityFields): string {
  if (isCommercialBoundReached(o)) return o.commercial!.bound === 'prepaid_block' ? BLOCK_USED_UP_LABEL : LIMIT_REACHED_LABEL
  return USED_UP_LABEL
}
